import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Radio, Search, FileText, Mic, Music, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 'generating', label: 'Writing the debate script', icon: FileText, detail: 'GPT-4o is crafting dialogue between your experts...' },
  { id: 'script_ready', label: 'Script complete', icon: CheckCircle2, detail: 'Preparing voice recordings...' },
  { id: 'generating_audio', label: 'Recording voices', icon: Mic, detail: 'Each expert is recording their lines in the studio...' },
  { id: 'ready', label: 'Going live!', icon: Radio, detail: 'Your episode is ready to play' },
];

const FLAVOR_MESSAGES = [
  "Experts are forming their opinions...",
  "The panel is getting heated already...",
  "Searching for latest real-world data...",
  "Mixing the debate audio...",
  "Final sound check in progress...",
  "Almost there — syncing segments...",
];

const LoadingPage = () => {
  const [status, setStatus] = useState('generating');
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [flavorMsg, setFlavorMsg] = useState(FLAVOR_MESSAGES[0]);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { podcastId } = useParams();
  const isMounted = useRef(true);
  const startTime = useRef(Date.now());
  const flavorIdx = useRef(0);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Elapsed timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (isMounted.current) setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate flavor messages
  useEffect(() => {
    const interval = setInterval(() => {
      flavorIdx.current = (flavorIdx.current + 1) % FLAVOR_MESSAGES.length;
      if (isMounted.current) setFlavorMsg(FLAVOR_MESSAGES[flavorIdx.current]);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Poll for podcast status
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/podcasts/${podcastId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const podcast = response.data.podcast;

        if (!isMounted.current) return;
        setStatus(podcast.status);

        if (podcast.status === 'ready') {
          setTimeout(() => navigate(`/player/${podcastId}`), 1200);
          return;
        }

        if (podcast.status === 'failed' || podcast.status === 'audio_failed') {
          setError(podcast.error || 'Generation failed. Please try again.');
          return;
        }

        setTimeout(pollStatus, 1500);
      } catch (err) {
        if (isMounted.current) setTimeout(pollStatus, 4000);
      }
    };

    pollStatus();
  }, [podcastId, token, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStepIdx = STEPS.findIndex(s => s.id === status);
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const getProgressPct = () => {
    if (status === 'ready') return 100;
    if (status === 'generating_audio') return Math.min(92, 40 + elapsed * 1.2);
    if (status === 'script_ready') return Math.min(39, 35 + elapsed * 0.1);
    return Math.min(35, elapsed * 2.5);
  };
  const progressPct = getProgressPct();

  const getElapsedHint = () => {
    if (status === 'ready') return ' · done!';
    if (status === 'generating_audio') return ' · voices recording...';
    if (status === 'script_ready') return ' · preparing voices...';
    if (status === 'generating') return ' · writing script...';
    return '';
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative" style={{ backgroundColor: '#0A0A0B' }}>
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(109,40,217,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(109,40,217,0.4) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} />

      <div className="relative z-10 text-center px-6 w-full max-w-md">
        {/* ON AIR indicator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
          style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}
        >
          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-bold tracking-wider text-red-400">GOING LIVE</span>
        </motion.div>

        {/* Waveform animation */}
        <div className="flex items-center justify-center gap-1 mb-6 h-14">
          {Array.from({ length: 24 }).map((_, i) => (
            <motion.div
              key={i}
              className="w-1 rounded-full"
              style={{ backgroundColor: '#6D28D9' }}
              animate={{
                height: [8, Math.random() * 50 + 10, 8],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 1.2 + Math.random() * 0.8,
                repeat: Infinity,
                delay: i * 0.05,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        {/* Flavor message */}
        <AnimatePresence mode="wait">
          <motion.p
            key={flavorMsg}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="text-lg mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F8FAFC' }}
          >
            {flavorMsg}
          </motion.p>
        </AnimatePresence>

        {/* Elapsed timer */}
        <p className="text-xs mb-6" style={{ color: '#64748B' }}>
          {formatTime(elapsed)} elapsed{getElapsedHint()}
        </p>

        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full mb-8 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #6D28D9, #A78BFA)' }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>

        {/* Step tracker */}
        <div className="space-y-3 text-left mb-8">
          {STEPS.map((step, i) => {
            const isDone = i < currentStepIdx || status === 'ready';
            const isCurrent = i === currentStepIdx && status !== 'ready';
            const Icon = step.icon;

            const getIconBgClass = () => {
              if (isDone) return 'bg-green-500/15';
              if (isCurrent) return 'bg-purple-500/15';
              return 'bg-white/5';
            };

            const getTextColorClass = () => {
              if (isDone) return 'text-green-400';
              if (isCurrent) return 'text-white';
              return 'text-gray-600';
            };

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 py-2 px-3 rounded-lg"
                style={{
                  backgroundColor: isCurrent ? 'rgba(109,40,217,0.08)' : 'transparent',
                  borderLeft: isCurrent ? '2px solid #6D28D9' : '2px solid transparent',
                }}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${getIconBgClass()}`}>
                  {isDone ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                  ) : isCurrent ? (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                      <Icon className="w-3.5 h-3.5 text-purple-400" />
                    </motion.div>
                  ) : (
                    <Icon className="w-3.5 h-3.5 text-gray-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${getTextColorClass()}`}>
                    {step.label}
                  </p>
                  {isCurrent && (
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] text-gray-500 mt-0.5">
                      {step.detail}
                    </motion.p>
                  )}
                </div>
                {isDone && <span className="text-[9px] text-green-500/60 font-medium">DONE</span>}
                {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />}
              </motion.div>
            );
          })}
        </div>

        {/* Error state */}
        {error && (
          <div className="p-4 rounded-xl max-w-md mx-auto" style={{ backgroundColor: 'rgba(239,68,68,0.1)' }}>
            <p className="text-red-400 text-sm mb-3">{error}</p>
            <button onClick={() => navigate('/')} className="text-sm text-purple-400 hover:text-purple-300 underline">
              Try a different topic
            </button>
          </div>
        )}

        {/* Floating Radio icon */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Radio className="w-6 h-6 mx-auto" style={{ color: '#6D28D9', opacity: 0.3 }} />
        </motion.div>
      </div>
    </div>
  );
};

export default LoadingPage;
